import axios, { AxiosResponse } from "axios";
import { DESIGN_CHANGED_EVENT } from "./issues-model";

/**
 * Project validation covers far more than the screen canvas (workflows, scheduled jobs, API
 * builder, datasource actions, data types…), but the Issues control's staleness signal
 * (DESIGN_CHANGED) only fired from canvas re-renders — so fixing a workflow never invalidated
 * the last validation result and the validate-on-open dirty gate refused to re-run
 * (KOA-15840). This interceptor widens the signal at the one choke point every designer
 * surface already goes through: a SUCCESSFUL mutating request to a validatable route marks
 * the last validation stale.
 *
 * Route ALLOWLIST, not "every mutation": chat/presence/telemetry/billing traffic must not
 * thrash the gate, and the validation endpoint itself is a POST that must never mark its own
 * result stale. A false-dirty costs one redundant run (capped by the 60s cooldown, client
 * single-flight and the server's cache); a false-fresh serves stale diagnostics — so when in
 * doubt a route belongs ON the list, and future mutations under these prefixes are covered
 * automatically.
 */
const VALIDATABLE_ROUTE_PREFIXES: string[] = [
  // BPMN processes: create/update/delete/duplicate/(in)activate + version lifecycle.
  "/workflow/",
  // Workflow Screens wizard: creates task/start screens and patches project wiring.
  "/workflowscreen/",
  "/scheduledjobs/",
  "/apimanagement/",
  // Kuika Data Types catalog (dataTypeName validations).
  "/domains/datatypes",
  // SQL + native actions (save/delete) and datasource removal.
  "/datasource/action",
  "/datasource/delete",
  // Screen create/delete/save — canvas edits already dirty via renderFrame, but panel/AI
  // flows on other dashboards mutate screens without ever rendering the canvas.
  "/screendesign/"
];

// ASCII route paths — plain lowercasing is locale-independent in JS, and comparing lowered
// strings sidesteps the mixed-casing the services actually use (/scheduledJobs, /apiManagement).
const MUTATING_METHODS: readonly string[] = ["post", "put", "delete", "patch"];

/** Pure matcher (exported for tests): does this request invalidate the last validation? */
export const isValidationDirtyingRequest = (method: string | undefined, url: string | undefined): boolean => {
  if (!method || !url) return false;
  if (!MUTATING_METHODS.includes(method.toLowerCase())) return false;
  const lowered: string = url.toLowerCase();
  // Never self-dirty: the validation run itself is a POST on this route.
  if (lowered.includes("/projectvalidation")) return false;
  return VALIDATABLE_ROUTE_PREFIXES.some((prefix: string) => lowered.includes(prefix.toLowerCase()));
};

/**
 * Registered once from index.tsx (same pattern as auth/interceptors). Success-only: a
 * rejected mutation changed nothing worth re-validating, and the error path must stay
 * untouched for the auth interceptor's refresh flow.
 */
export const registerValidationDirtyInterceptor = (): void => {
  axios.interceptors.response.use((response: AxiosResponse): AxiosResponse => {
    if (isValidationDirtyingRequest(response.config?.method, response.config?.url)) {
      window.dispatchEvent(new Event(DESIGN_CHANGED_EVENT));
    }
    return response;
  });
};
